import { useCallback, useState } from "react";
import { useEngineContext } from "../app/EngineProvider";

export interface EngineActions {
  start: () => void;
  pause: () => void;
  resume: () => void;
  reset: () => void;
  panic: () => void;
  arm: () => void;
  retry: () => void;
  busy: boolean;
}

/**
 * Wraps the engine lifecycle for the UI.
 *
 * `busy` covers the async paths (start, and resume while it waits for old
 * reservations to drain) so buttons can be disabled and double-triggering is
 * impossible from the UI as well as from the engine's own status guards.
 */
export function useAudioEngine(): EngineActions {
  const { ensureEngine, getEngine, errorLog } = useEngineContext();
  const [busy, setBusy] = useState(false);

  const start = useCallback(() => {
    const engine = ensureEngine();
    setBusy(true);
    engine
      .init()
      .catch(() => {
        // Already recorded by the engine; surfaced via Diagnostics.lastError.
      })
      .finally(() => {
        setBusy(false);
      });
  }, [ensureEngine]);

  const resume = useCallback(() => {
    const engine = getEngine();
    if (engine === null) return;
    setBusy(true);
    engine
      .resume()
      .catch((error: unknown) => {
        errorLog.record("ui:resume", error);
      })
      .finally(() => {
        setBusy(false);
      });
  }, [getEngine, errorLog]);

  return {
    start,
    resume,
    busy,
    pause: useCallback(() => {
      getEngine()?.pause();
    }, [getEngine]),
    reset: useCallback(() => {
      getEngine()?.reset();
    }, [getEngine]),
    panic: useCallback(() => {
      getEngine()?.panic();
    }, [getEngine]),
    arm: useCallback(() => {
      getEngine()?.arm();
    }, [getEngine]),
    retry: useCallback(() => {
      // A failed instance is terminal; ensureEngine() replaces it.
      const engine = ensureEngine();
      setBusy(true);
      engine
        .init()
        .catch(() => {
          /* recorded by the engine */
        })
        .finally(() => {
          setBusy(false);
        });
    }, [ensureEngine]),
  };
}
